import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";

export const STORAGE_CALCULATION_METHOD_OPTIONS = [
  {
    value: "warehouse_wide_highmark",
    label: "Warehouse-Wide Highmark",
    description: "Choose this when storage should be billed from the busiest point across the entire warehouse during the billing period. This works best when customers are charged from one overall peak storage count.",
    Icon: WarehouseOutlinedIcon,
    iconStyles: {
      color: "#B7791F",
      backgroundColor: "rgba(183, 121, 31, 0.12)",
    },
  },
  {
    value: "total_throughput",
    label: "Total Throughput (Opening + Inbound)",
    description: "Choose this when storage should count everything already in storage at the start of the period plus everything received during the period. This works best when each item that touches storage should be counted once.",
    Icon: Inventory2OutlinedIcon,
    iconStyles: {
      color: "#1976D2",
      backgroundColor: "rgba(25, 118, 210, 0.12)",
    },
  },
  {
    value: "highmark",
    label: "Highmark (Peak Quantity)",
    description: "Choose this when each item should be billed by its own highest warehouse quantity during the billing period. This works best when items move in and out at different times and should be measured individually.",
    Icon: TrendingUpOutlinedIcon,
    iconStyles: {
      color: "#2E7D32",
      backgroundColor: "rgba(46, 125, 50, 0.12)",
    },
  },
];

export const STORAGE_CALCULATION_METHOD_DESCRIPTIONS = STORAGE_CALCULATION_METHOD_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.description;
  return acc;
}, {});

export const STORAGE_CALCULATION_METHOD_LABELS = STORAGE_CALCULATION_METHOD_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

export default function StorageCalculationMethodSelectionModal({ open, onClose, currentValue, onSelect }) {
  const [selected, setSelected] = useState(currentValue);

  useEffect(() => {
    if (open) {
      setSelected(currentValue);
    }
  }, [currentValue, open]);

  const options = useMemo(() => STORAGE_CALCULATION_METHOD_OPTIONS, []);

  const handleCancel = () => {
    setSelected(currentValue);
    onClose();
  };

  const handleSelect = value => {
    setSelected(value);
    onSelect(value);
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      onClose={handleCancel}
      PaperProps={{ sx: { width: "620px", maxWidth: "92vw" } }}
    >
      <DialogContent sx={{ bgcolor: "#f4f6f8" }}>
        <DialogContentText>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box width={40} />
            <Typography fontSize={22} fontWeight={400} textAlign="center" color="text.primary">
              Select Storage Method
            </Typography>
            <IconButton color="textPrimary" onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box display="flex" flexDirection="column" gap={1.5}>
            {options.map(option => {
              const isSelected = selected === option.value;
              const Icon = option.Icon;

              return (
                <Paper
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  elevation={isSelected ? 2 : 0}
                  sx={{
                    cursor: "pointer",
                    border: "2px solid",
                    borderColor: isSelected ? "primary.main" : "transparent",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                    boxShadow: isSelected ? "0 0 0 1px rgba(25, 118, 210, 0.08)" : "none",
                    "&:hover": {
                      borderColor: isSelected ? "primary.main" : "grey.300",
                    },
                  }}
                >
                  <Box p={2} display="flex" alignItems="center" gap={1.5}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: option.iconStyles.backgroundColor,
                        color: option.iconStyles.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography fontWeight={500} fontSize={15} color="text.primary">
                        {option.label}
                      </Typography>
                      <Typography fontSize={14} color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
}
